/**
 * SourceIcons — compact icon-only source picker rendered in the CreatorSection
 * tab nav row. Replaces the heavier SourceCards row that used to sit above the
 * player. Each platform is a small icon button; the active one is highlighted
 * and a live dot appears when /live-status reports the platform as live AND
 * the data isn't stale.
 *
 * Single-platform creators don't get any icons — the parent only renders this
 * when there are 2+ configured platforms.
 *
 * Stale-LIVE guard: even if a platform's isLive flag is true, we suppress the
 * live dot when the live-status query hasn't refreshed in STALE_LIVE_THRESHOLD
 * ms. Prevents a stuck-true backend flag (EventSub never fired the offline
 * transition) from showing a misleading LIVE indicator hours after the stream
 * actually ended.
 */

import React from "react";
import { FaCircle } from "react-icons/fa";
import type { FeaturedStreamResponse } from "@/hooks/hub/useGetHub";
import { platformIcon, platformLabel, asStreamingSource, type StreamingSource } from "./shared";

/*
 * 10 min = ~10 missed 60s polls before we treat isLive as stuck. useGetHubLiveStatus
 * refetches every 60s while the tab is active, so dataUpdatedAt should normally be
 * <60s old. A 10-minute gap means the tab was backgrounded or the network was down
 * — either way the isLive flag is not trustworthy and we suppress the dot.
 */
const STALE_LIVE_THRESHOLD_MS = 10 * 60 * 1000;

interface SourceIconsProps {
    available: FeaturedStreamResponse[];
    activePlatform?: StreamingSource | null;
    onSelect: (platform: StreamingSource) => void;
    /** ms timestamp from the live-status query — used for staleness check. */
    liveStatusFetchedAt?: number;
}

export const SourceIcons: React.FC<SourceIconsProps> = ({ available, activePlatform, onSelect, liveStatusFetchedAt }) => {
    const isStale = liveStatusFetchedAt
        ? Date.now() - liveStatusFetchedAt > STALE_LIVE_THRESHOLD_MS
        : false;

    return (
        <div className="hcs-source-icons" role="tablist" aria-label="Streaming sources">
            {available.map(s => {
                /*
                 * Narrowed, not cast. `platform` is this tab's React key, its
                 * active-state comparison and the value handed to `onSelect` —
                 * `as StreamingSource` on an optional four-valued field meant a
                 * channel-less member rendered a key of `undefined` and could
                 * select a platform that does not exist. A source with no
                 * single platform is not a source you can switch to, so it
                 * renders no icon.
                 */
                const platform = asStreamingSource(s.platform);
                if (!platform) return null;
                const isActive = activePlatform === platform;
                /*
                 * Tighter live-signal heuristic: backend /live-status can get
                 * stuck on isLive=true if a webhook offline-transition was
                 * dropped. A real live stream has at least the streamer's own
                 * viewer (count > 0); a 0-viewer "live" is almost always the
                 * stuck-flag case. Combined with the dataUpdatedAt staleness
                 * gate this suppresses the dot in both failure modes.
                 */
                const showLive = s.isLive && !isStale && (s.viewerCount ?? 0) > 0;
                return (
                    <button
                        key={platform}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        aria-label={`${platformLabel(platform)}${showLive ? " (live)" : ""}`}
                        title={`${platformLabel(platform)}${showLive ? " — LIVE" : ""}`}
                        className={`hcs-source-icon${isActive ? " is-active" : ""}${showLive ? " is-live" : ""}`}
                        onClick={() => onSelect(platform)}
                    >
                        <span className="hcs-source-icon__glyph">{platformIcon(platform)}</span>
                        {showLive && <FaCircle className="hcs-source-icon__dot" aria-hidden />}
                    </button>
                );
            })}
        </div>
    );
};
